const DB_NAME = "AudioDB"
const STORE_NAME = "audioFiles"

// Opens a connection to the IndexedDB database
// Returns null if IndexedDB is unavailable (e.g. iOS private browsing)
function openDB(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest
    try {
      request = window.indexedDB.open(DB_NAME, 1)
    } catch {
      resolve(null)
      return
    }

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db: IDBDatabase = (event.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" })
      }
    }

    request.onsuccess = (event: Event) => {
      resolve((event.target as IDBOpenDBRequest).result)
    }

    request.onerror = () => {
      resolve(null)
    }
  })
}

// Function to store an audio file in the database
async function storeFile(filename: string, content: Blob): Promise<void> {
  const db = await openDB()
  if (!db) return
  const tx: IDBTransaction = db.transaction(STORE_NAME, "readwrite")
  const store: IDBObjectStore = tx.objectStore(STORE_NAME)

  await new Promise<void>((resolve, reject) => {
    const request = store.put({ id: filename, content })
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })

  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

// Function to retrieve an audio file from the database by its filename
// Returns null if IndexedDB is unavailable
async function getAudioFileFromDB(filename: string): Promise<Blob | null> {
  const db = await openDB()
  if (!db) return null

  const tx: IDBTransaction = db.transaction(STORE_NAME, "readonly")
  const store: IDBObjectStore = tx.objectStore(STORE_NAME)

  return new Promise<Blob | null>((resolve, reject) => {
    const request = store.get(filename)
    request.onsuccess = () => {
      if (!request.result) {
        reject(new Error(`File not found: ${filename}`))
      } else {
        resolve(request.result.content)
      }
    }
    request.onerror = () => {
      reject(request.error)
    }
  })
}

// Function to clear all audio files from the database
// Returns false if IndexedDB is unavailable
async function clearAudioFiles(): Promise<boolean> {
  const db = await openDB()
  if (!db) return false

  const tx: IDBTransaction = db.transaction(STORE_NAME, "readwrite")
  const store: IDBObjectStore = tx.objectStore(STORE_NAME)

  await new Promise<void>((resolve, reject) => {
    const request = store.clear()
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })

  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })

  return true
}

export { openDB, storeFile, getAudioFileFromDB, clearAudioFiles }
